import { useState, type ReactElement } from 'react';
import { Combobox, InputBase, useCombobox } from '@mantine/core';

/**
 * A searchable Select that also accepts free-text entry — the seeded
 * `land_lookups` list (spec §2.2) is a best-effort import from the legacy
 * Access DB, so an existing lease can carry a value that isn't in the
 * dropdown, and a reviewer may need to record a genuinely new one. Built on
 * Mantine's `Combobox` primitive since the plain `Select` only ever commits
 * an exact list match.
 *
 * Split into its own file (rather than living in lease-fields.tsx) so the
 * registry module only exports plain data — a real component defined
 * alongside it confuses `react-refresh/only-export-components`.
 */
const CREATE_OPTION = '__lease_lookup_create__';

export function CreatableLookupSelect({
  label,
  options,
  value,
  onChange,
  disabled,
}: {
  label: string;
  options: string[];
  value: string | null;
  onChange: (v: string | null) => void;
  disabled: boolean;
}): ReactElement {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [search, setSearch] = useState(value ?? '');

  // Adjust state during render (not useEffect) when the external value
  // changes out from under us — e.g. the drawer switches to a different
  // lease via `form.reset()`. Typing only ever touches `search`, so this
  // doesn't fire on every keystroke.
  const [trackedValue, setTrackedValue] = useState(value);
  if (value !== trackedValue) {
    setTrackedValue(value);
    setSearch(value ?? '');
  }

  const trimmed = search.trim();
  const exactMatch = options.some((o) => o === trimmed);
  const filtered =
    trimmed === '' ? options : options.filter((o) => o.toLowerCase().includes(trimmed.toLowerCase()));

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        const next = val === CREATE_OPTION ? trimmed : val;
        onChange(next === '' ? null : next);
        setSearch(next);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          label={label}
          value={search}
          disabled={disabled}
          rightSection={<Combobox.Chevron />}
          rightSectionPointerEvents="none"
          onChange={(event) => {
            setSearch(event.currentTarget.value);
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => {
            combobox.closeDropdown();
            if (trimmed === '') {
              onChange(null);
            } else {
              onChange(trimmed);
              setSearch(trimmed);
            }
          }}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options mah={220} style={{ overflowY: 'auto' }}>
          {filtered.map((o) => (
            <Combobox.Option value={o} key={o} active={o === value}>
              {o}
            </Combobox.Option>
          ))}
          {!exactMatch && trimmed !== '' && (
            <Combobox.Option value={CREATE_OPTION}>{`+ Use "${trimmed}"`}</Combobox.Option>
          )}
          {filtered.length === 0 && trimmed === '' && <Combobox.Empty>No options</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
